import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersApi, permissionsApi } from '../services/api';
import { User } from '../types';
import {
    ArrowLeft,
    Shield,
    User as UserIcon,
    Plus,
    Minus,
    Check,
    X,
    Edit2,
    Save,
    AlertTriangle,
    Search,
    Filter,
    MoreHorizontal,
    Clock,
    Info,
    Lock,
    Unlock,
    Crown,
    Eye,
    EyeOff,
    RefreshCw
} from 'lucide-react';
import PageContainer from '../components/PageContainer';
import {
    Button,
    DataToolbar,
    EmptyState,
    LoadingState,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    StatCard,
    StatusBadge,
    useToast,
    DateInput,
    Input,
    todayDateKey,
} from '../components/ui';

// Temporary interfaces until we fix backend types
interface Permission {
    resource: string;
    action: string;
    description?: string;
}

interface UserPermissions {
    userId: string;
    role: string;
    rolePermissions: string[];
    userPermissions: { permission: string; granted: boolean }[];
    effectivePermissions: string[];
}

interface PermissionGroup {
    resource: string;
    permissions: {
        action: string;
        description?: string;
        hasFromRole: boolean;
        userOverride?: boolean;
        effective: boolean;
    }[];
}

const UserPermissionsPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Core data states
    const [user, setUser] = useState<User | null>(null);
    const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
    const [allPermissions, setAllPermissions] = useState<Permission[]>([]);

    // UI states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedResource, setSelectedResource] = useState<string>('all');
    const [showOnlyModified, setShowOnlyModified] = useState(false);

    // Modal states
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState<string>('');
    const [reason, setReason] = useState('');
    const [expiresDate, setExpiresDate] = useState('');
    const [expiresTime, setExpiresTime] = useState('23:59');

    useEffect(() => {
        if (userId) {
            loadAllData();
        }
    }, [userId]);

    const loadAllData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [userResponse, permissionsResponse, allPermsResponse] = await Promise.allSettled([
                usersApi.getById(userId!),
                permissionsApi.getUserPermissions(userId!),
                permissionsApi.getAllPermissions()
            ]);

            if (userResponse.status === 'fulfilled') {
                setUser(userResponse.value.data.data);
            } else {
                throw new Error('Failed to load user data');
            }

            if (permissionsResponse.status === 'fulfilled') {
                setUserPermissions(permissionsResponse.value.data);
            }

            if (allPermsResponse.status === 'fulfilled') {
                setAllPermissions(allPermsResponse.value.data || []);
            }

        } catch (error: any) {
            console.error('Error loading data:', error);
            setError(error?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const organizePermissions = (): PermissionGroup[] => {
        if (!allPermissions.length || !userPermissions) return [];

        const groups: { [key: string]: PermissionGroup } = {};

        allPermissions.forEach(perm => {
            const resource = perm.resource;
            if (!groups[resource]) {
                groups[resource] = {
                    resource,
                    permissions: []
                };
            }

            const permissionString = `${perm.resource}:${perm.action}`;
            const hasFromRole = userPermissions.rolePermissions.includes(permissionString);
            const userOverride = userPermissions.userPermissions.find(up => up.permission === permissionString);
            const effective = userPermissions.effectivePermissions.includes(permissionString);

            groups[resource].permissions.push({
                action: perm.action,
                description: perm.description,
                hasFromRole,
                userOverride: userOverride?.granted,
                effective
            });
        });

        return Object.values(groups).sort((a, b) => a.resource.localeCompare(b.resource));
    };

    const filteredGroups = organizePermissions().filter(group => {
        if (selectedResource !== 'all' && group.resource !== selectedResource) return false;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const matchesResource = group.resource.toLowerCase().includes(term);
            const matchesAction = group.permissions.some(p => p.action.toLowerCase().includes(term));
            const matchesDescription = group.permissions.some(p => p.description?.toLowerCase().includes(term));

            if (!matchesResource && !matchesAction && !matchesDescription) return false;
        }

        if (showOnlyModified) {
            const hasModifications = group.permissions.some(p => p.userOverride !== undefined);
            if (!hasModifications) return false;
        }

        return true;
    });

    const handleGrantPermission = async () => {
        if (!selectedPermission || !userId) return;

        try {
            setSaving(true);
            const [resource, action] = selectedPermission.split(':');

            await permissionsApi.grantPermission(userId, {
                resource,
                action,
                reason: reason || undefined,
                expiresAt: expiresDate ? `${expiresDate}T${/^\d{1,2}:\d{2}$/.test(expiresTime) ? expiresTime.padStart(5, '0') : '23:59'}` : undefined
            });

            await loadAllData();
            setShowGrantModal(false);
            setSelectedPermission('');
            setReason('');
            setExpiresDate('');
            setExpiresTime('23:59');

            showToast('Permission granted successfully', 'success');
        } catch (error: any) {
            showToast(error.message || 'Failed to grant permission', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRevokePermission = async () => {
        if (!selectedPermission || !userId) return;

        try {
            setSaving(true);
            const [resource, action] = selectedPermission.split(':');

            await permissionsApi.revokePermission(userId, {
                resource,
                action,
                reason: reason || undefined
            });

            await loadAllData();
            setShowRevokeModal(false);
            setSelectedPermission('');
            setReason('');

            showToast('Permission revoked successfully', 'success');
        } catch (error: any) {
            showToast(error.message || 'Failed to revoke permission', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveOverride = async (resource: string, action: string) => {
        if (!userId) return;

        try {
            setSaving(true);
            await permissionsApi.removePermissionOverride(userId, { resource, action });
            await loadAllData();
            showToast('Permission override removed', 'success');
        } catch (error: any) {
            showToast(error.message || 'Failed to remove override', 'error');
        } finally {
            setSaving(false);
        }
    };

    const getResourceIcon = (resource: string) => {
        switch (resource) {
            case 'user': return <UserIcon className="w-4 h-4" />;
            case 'campus': return <Shield className="w-4 h-4" />;
            case 'class': return <UserIcon className="w-4 h-4" />;
            case 'student': return <UserIcon className="w-4 h-4" />;
            case 'attendance': return <Clock className="w-4 h-4" />;
            case 'dashboard': return <Shield className="w-4 h-4" />;
            default: return <Shield className="w-4 h-4" />;
        }
    };

    const getPermissionStatus = (permission: any) => {
        if (permission.userOverride !== undefined) {
            return permission.userOverride ? 'granted' : 'denied';
        }
        return permission.hasFromRole ? 'inherited' : 'none';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'granted': return 'text-success-600 bg-success-50 border-success-200';
            case 'denied': return 'text-danger-600 bg-danger-50 border-danger-200';
            case 'inherited': return 'text-academy-600 bg-academy-50 border-academy-200';
            default: return 'text-ink-600 bg-ink-50 border-ink-200';
        }
    };

    const getStatusTone = (status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
        switch (status) {
            case 'granted': return 'success';
            case 'denied': return 'danger';
            case 'inherited': return 'info';
            default: return 'neutral';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'granted': return <Check className="w-4 h-4" />;
            case 'denied': return <X className="w-4 h-4" />;
            case 'inherited': return <Crown className="w-4 h-4" />;
            default: return <Minus className="w-4 h-4" />;
        }
    };

    if (loading) {
        return (
            <PageContainer title="Phân quyền người dùng" subtitle="Đang tải dữ liệu phân quyền.">
                <SectionPanel><LoadingState title="Loading user permissions..." /></SectionPanel>
            </PageContainer>
        );
    }

    if (error || !user) {
        return (
            <PageContainer title="Phân quyền người dùng" subtitle="Không thể tải dữ liệu phân quyền.">
                <SectionPanel className="mx-auto max-w-md p-6">
                    <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-ink-900 text-center mb-2">
                        {!user ? 'User Not Found' : 'Error Loading Permissions'}
                    </h2>
                    <p className="text-ink-600 text-center mb-6">
                        {error || 'The requested user could not be found.'}
                    </p>
                    <Button
                        className="w-full"
                        onClick={() => navigate('/users')}
                        icon={<ArrowLeft className="h-4 w-4" />}
                    >
                        Back to Users
                    </Button>
                </SectionPanel>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title={`${user.firstName} ${user.lastName}`}
            subtitle={`${user.email} - ${user.role}`}
            actions={(
                <>
                    <Button variant="secondary" icon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/users')}>
                        Back to Users
                    </Button>
                    <Button variant="secondary" icon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />} onClick={loadAllData} disabled={loading}>
                        Refresh
                    </Button>
                </>
            )}
        >

                {/* Stats Cards */}
                {userPermissions && (
                    <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-2 xl:grid-cols-4">
                        <StatCard label="Role Permissions" value={userPermissions.rolePermissions.length} detail="Inherited" icon={<Crown className="h-5 w-5" />} tone="academy" />
                        <StatCard label="User Overrides" value={userPermissions.userPermissions.length} detail="Direct changes" icon={<Edit2 className="h-5 w-5" />} tone="action" />
                        <StatCard label="Effective Total" value={userPermissions.effectivePermissions.length} detail="Current access" icon={<Shield className="h-5 w-5" />} tone="academy" />
                        <StatCard label="Available Total" value={allPermissions.length} detail="Permission catalog" icon={<Lock className="h-5 w-5" />} tone="warning" />
                    </div>
                )}

                {/* Controls */}
                <DataToolbar
                    className="sm:items-center"
                    controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center"
                    actions={(
                        <>
                            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowGrantModal(true)}>
                                Grant Permission
                            </Button>
                            <Button variant="danger" icon={<Minus className="h-4 w-4" />} onClick={() => setShowRevokeModal(true)}>
                                Revoke Permission
                            </Button>
                        </>
                    )}
                >
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search permissions…"
                        leftIcon={<Search className="h-4 w-4" />}
                        aria-label="Search permissions"
                        className="min-h-9 lg:w-72"
                    />
                    <select
                        value={selectedResource}
                        onChange={(e) => setSelectedResource(e.target.value)}
                        aria-label="Resource"
                        className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                    >
                        <option value="all">All Resources</option>
                        {[...new Set(allPermissions.map(p => p.resource))].map(resource => (
                            <option key={resource} value={resource}>{resource}</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-ink-600">
                        <input
                            type="checkbox"
                            checked={showOnlyModified}
                            onChange={(e) => setShowOnlyModified(e.target.checked)}
                            className="rounded border-ink-300 text-academy-600 focus:ring-academy-200"
                        />
                        <span>Show only modified</span>
                    </label>
                </DataToolbar>

                {/* Permissions Grid */}
                <ResponsiveDataView desktopLabel="Danh sách phân quyền" mobileLabel="Thẻ phân quyền" desktop={null} mobile={null} className="hidden" />
                <div className="space-y-6">
                    {filteredGroups.map(group => (
                        <SectionPanel key={group.resource} className="overflow-hidden">
                            <div className="border-b border-ink-200 bg-ink-50 px-4 py-4 sm:px-5">
                                <div className="flex items-center space-x-3">
                                    {getResourceIcon(group.resource)}
                                    <h3 className="text-base font-semibold text-ink-900 capitalize">
                                        {group.resource} Permissions
                                    </h3>
                                    <StatusBadge tone="info">{group.permissions.length}</StatusBadge>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {group.permissions.map(permission => {
                                        const status = getPermissionStatus(permission);
                                        const permissionString = `${group.resource}:${permission.action}`;

                                        return (
                                            <div key={permission.action} className="rounded-lg border border-ink-200 bg-white p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center space-x-2">
                                                        <StatusBadge tone={getStatusTone(status)} icon={getStatusIcon(status)}>
                                                            {status === 'inherited' ? 'From Role' :
                                                                status === 'granted' ? 'User Granted' :
                                                                    status === 'denied' ? 'User Denied' : 'Not Granted'}
                                                        </StatusBadge>
                                                        <span className="font-medium">
                                                            {permission.action}
                                                        </span>
                                                    </div>
                                                    {permission.userOverride !== undefined && (
                                                        <button
                                                            onClick={() => handleRemoveOverride(group.resource, permission.action)}
                                                            disabled={saving}
                                                            className="text-ink-400 hover:text-ink-600 p-1 rounded"
                                                            title="Remove override"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                {permission.description && (
                                                    <p className="text-xs text-ink-600 mb-2">
                                                        {permission.description}
                                                    </p>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium capitalize">
                                                        {status === 'inherited' ? 'From Role' :
                                                            status === 'granted' ? 'User Granted' :
                                                                status === 'denied' ? 'User Denied' : 'Not Granted'}
                                                    </span>
                                                    <div className="flex items-center space-x-1">
                                                        {permission.effective ? (
                                                            <Check className="w-4 h-4 text-success-600" />
                                                        ) : (
                                                            <X className="w-4 h-4 text-danger-600" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </SectionPanel>
                    ))}
                </div>

                {filteredGroups.length === 0 && (
                    <SectionPanel>
                        <EmptyState title="No permissions found" description="Try adjusting your search terms or filters." />
                    </SectionPanel>
                )}


            <Modal
                open={showGrantModal}
                title="Grant Permission"
                onClose={() => setShowGrantModal(false)}
                className="sm:max-w-md"
                footer={(
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={() => setShowGrantModal(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGrantPermission}
                            disabled={saving || !selectedPermission}
                            loading={saving}
                            className="border-success-600 bg-success-600 hover:border-success-700 hover:bg-success-700"
                        >
                            {saving ? 'Granting...' : 'Grant Permission'}
                        </Button>
                    </div>
                )}
            >
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">
                                    Permission
                                </label>
                                <select
                                    value={selectedPermission}
                                    onChange={(e) => setSelectedPermission(e.target.value)}
                                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                >
                                    <option value="">Select permission</option>
                                    {allPermissions.map(perm => {
                                        const permString = `${perm.resource}:${perm.action}`;
                                        const isEffective = userPermissions?.effectivePermissions.includes(permString);
                                        return (
                                            <option key={permString} value={permString} disabled={isEffective}>
                                                {permString} {isEffective ? '(Already granted)' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">
                                    Reason (optional)
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Why is this permission being granted?"
                                    rows={3}
                                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                />
                            </div>
                            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
                                <DateInput
                                    label="Expires At (optional)"
                                    value={expiresDate}
                                    min={todayDateKey()}
                                    onChange={setExpiresDate}
                                />
                                <Input
                                    label="Giờ"
                                    inputMode="numeric"
                                    placeholder="HH:mm"
                                    maxLength={5}
                                    value={expiresTime}
                                    disabled={!expiresDate}
                                    onChange={(e) => setExpiresTime(e.target.value.replace(/[^\d:]/g, ''))}
                                />
                            </div>
                        </div>
            </Modal>

            <Modal
                open={showRevokeModal}
                title="Revoke Permission"
                onClose={() => setShowRevokeModal(false)}
                className="sm:max-w-md"
                footer={(
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={() => setShowRevokeModal(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleRevokePermission}
                            disabled={saving || !selectedPermission}
                            loading={saving}
                        >
                            {saving ? 'Revoking...' : 'Revoke Permission'}
                        </Button>
                    </div>
                )}
            >
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">
                                    Permission
                                </label>
                                <select
                                    value={selectedPermission}
                                    onChange={(e) => setSelectedPermission(e.target.value)}
                                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                >
                                    <option value="">Select permission</option>
                                    {allPermissions.map(perm => {
                                        const permString = `${perm.resource}:${perm.action}`;
                                        const isEffective = userPermissions?.effectivePermissions.includes(permString);
                                        return (
                                            <option key={permString} value={permString} disabled={!isEffective}>
                                                {permString} {!isEffective ? '(Not granted)' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">
                                    Reason (optional)
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Why is this permission being revoked?"
                                    rows={3}
                                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                />
                            </div>
                        </div>
            </Modal>
        </PageContainer>
    );
};

export default UserPermissionsPage;
